import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { NotificationManager, PaymentV2Api } from '@atlas/core';
import { Button, DefaultPopup, Input, Label } from '@atlas/ui';

import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

const WithdrawalInterkassaCardPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;
  const [cardNumber, setCardNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const { amount, currency } = qs.parse(redirectUrl);
  const [error, setError] = useState(null);
  const amountValue = useMemo(
    () => parseInt(typeof amount === 'string' ? amount : amount[0]),
    [amount],
  );
  const validateCardNumber = number => {
    const regex = /^[0-9]{16}$/;

    if (!regex.test(number)) {
      return {
        message: 'interkassa_withdrawal_length_validation_message',
        params: { ruleOption: 16 },
      };
    }

    return null;
  };

  const handleChangeCardNumber = useCallback(e => {
    const value = e.target.value.replace(/\D/g, '');

    const error = validateCardNumber(value);

    setError(error);
    setCardNumber(value);
  }, []);

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    const cleanedCardNumber = cardNumber.replace(/\s/g, '');

    const requestData = {
      additionalParameters: {
        card: cleanedCardNumber,
        publicPaymentId,
      },
      amount: amountValue,
      currency,
      pmId,
    };

    setIsSubmitting(true);

    try {
      const response = await PaymentV2Api.createAsyncWithdrawalV2(requestData);
      NotificationManager.display({
        data: {
          amount,
          currency,
          publicPaymentId: response?.publicPaymentId,
          paymentMethod,
        },
        message: 'successfulWithdrawal',
        type: 'successfulWithdrawal',
      });
    } catch (error) {
      NotificationManager.display({
        data: {
          errorType: true,
          headerTitle: 'withdrawal',
          text: 'withdrawal_interkassa_card_text_error',
          title: 'failed_sad_emoticon',
        },
        type: 'withdrawalResultPopup',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    cardNumber,
    publicPaymentId,
    amountValue,
    currency,
    pmId,
    amount,
    paymentMethod,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className="interkassa-modal"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="popup-content">
        <div className="popup-payment-method">
          <PaymentListItem amount={amountValue} payment={paymentMethod} showAmount />
        </div>
        <div className="popup-input-container with-error">
          <Input
            active={isActiveInput}
            className="popup-input"
            error={error}
            inputMode="numeric"
            label="interkassa_withdrawal_card_number"
            maxLength={16}
            onBlur={() => setIsActiveInput(false)}
            onChange={handleChangeCardNumber}
            onFocus={() => setIsActiveInput(true)}
            type="number"
            value={cardNumber}
          />
          {error && (
            <div className="help-text-container">
              <Label message={error.message} params={error.params} />
            </div>
          )}
        </div>
      </div>

      <div className="popup__footer">
        <Button
          className="primary big block"
          disabled={!cardNumber || isSubmitting || error}
          error={error}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalInterkassaCardPopup.propTypes = {
  data: PropTypes.object.isRequired,
  id: PropTypes.string,
};

export default WithdrawalInterkassaCardPopup;
